import cn from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import tinycolor from 'tinycolor2';

import { Star } from '@webapp/common/assets/icons';
import { ReactRating } from '@webapp/common/components/react-rating';
import { Scrollarea } from '@webapp/common/components/scrollarea';

import css from './rating.css';

export const stylePosition = {
    right: css.end,
    left: css.start,
    center: css.center,
};

export const Rating: FC<{
    color?: string;
    size?: number;
    count?: number;
    disabled?: boolean;
    defaultValue?: number;
    value?: number;
    onChange?: (value: number) => void;
    className?: string;
    flexStart?: boolean;
    isEmpty?: boolean;
    starsPosition?: string;
}> = ({
    className,
    color,
    count,
    defaultValue,
    disabled,
    flexStart,
    onChange,
    size = 1,
    isEmpty,
    value,
    starsPosition,
}) => {
    const [innerValue, setInnerValue] = useState(defaultValue || value);
    const isInit = useRef(false);
    const starsClass = starsPosition ? stylePosition[starsPosition] : '';

    useEffect(() => {
        if (!isInit.current) {
            isInit.current = true;
            return;
        }

        setInnerValue(value);
    }, [value]);

    const starClassName = useCallback(
        (filled) =>
            cn(css.item, {
                [css.sm]: size === 1,
                [css.md]: size === 2,
                [css.lg]: size === 3,
                [css.filled]: filled,
            }),
        [size],
    );

    const handleClick = useCallback((value) => {
        setInnerValue((prev) => (prev === value ? 0 : value));
        const el = document?.activeElement as HTMLInputElement;
        if (el?.blur) {
            // fixes focus handle on mobile
            el?.blur();
        }
    }, []);

    const emptySymbol = useMemo(
        () => <Star className={starClassName(false)} style={{ color: tinycolor(color).toRgbString() }} />,
        [color, starClassName],
    );

    const fullSymbol = useMemo(
        () => <Star className={starClassName(true)} style={{ color: tinycolor(color).toRgbString() }} />,
        [color, starClassName],
    );

    return (
        <div
            className={cn(css.rating, className, {
                [css.flexStart]: flexStart,
            })}
        >
            <Scrollarea vertical={false}>
                <ReactRating
                    className={cn(css.reactRating, starsClass)}
                    emptySymbol={emptySymbol}
                    fullSymbol={isEmpty ? emptySymbol : fullSymbol}
                    initialRating={innerValue}
                    readonly={disabled}
                    stop={count}
                    onChange={(value) => {
                        setInnerValue(value);
                        onChange?.(value);
                    }}
                    onClick={handleClick}
                />
            </Scrollarea>
        </div>
    );
};
